// Lib
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { get, startsWith } from 'lodash/fp';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Actions
import { currentBoardIdSet } from '../reducers/currentBoardId/currentBoardIdActions';
import { replaceBoardUrl } from '../reducers/navigationActions';

// Selectors
import { getCurrentBoardId, getCurrentBoard } from '../reducers/currentBoardId/currentBoardIdSelector';
import { getIsCurrentVisibleBoardError } from '../element/board/boardSelector';
import { getUrlPermissionId } from './routingSelector';
import { getCurrentBoardBestUrlPermissionId } from '../utils/permissions/elementPermissionsSelector';
import { getElementId, getTitle } from '../../common/elements/utils/elementPropertyUtils';
import { slugify } from '../../common/utils/stringUtil';

const getRouteSlug = get('slug');
const getPathname = get(['location', 'pathname']);
const startsWithPreview = startsWith('/preview');
const startsWithPublishPreview = startsWith('/publish-preview');

const mapStateToProps = createStructuredSelector({
    isCurrentBoardError: getIsCurrentVisibleBoardError,
    stateCurrentBoardId: getCurrentBoardId,
    currentBoard: getCurrentBoard,
    isPreviewRoute: (_, props) => startsWithPreview(getPathname(props)),
    isPublishPreviewRoute: (_, props) => startsWithPublishPreview(getPathname(props)),
    currentUrlPermissionId: getUrlPermissionId,
    bestUrlPermissionId: getCurrentBoardBestUrlPermissionId,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchCurrentBoardIdSet: (boardId) => dispatch(currentBoardIdSet({ boardId })),
    dispatchReplaceBoardUrl: ({ boardId, slug, permissionId }) =>
        dispatch(replaceBoardUrl({ boardId, slug, permissionId })),
});

const CurrentBoardIdSynchroniser = ({
    params,
    currentBoard,
    routeParams,
    isPreviewRoute,
    isPublishPreviewRoute,
    stateCurrentBoardId,
    dispatchCurrentBoardIdSet,
    dispatchReplaceBoardUrl,
    isCurrentBoardError,
    currentUrlPermissionId,
    bestUrlPermissionId,
}) => {
    const lastBoardId = useRef();
    const { currentBoardId } = params;
    const routeSlug = getRouteSlug(routeParams);

    // if currentBoardId from route is different from currentBoardId in state,
    // update the state to reflect the route
    useEffect(() => {
        if (currentBoardId && currentBoardId !== lastBoardId.current && currentBoardId !== stateCurrentBoardId) {
            dispatchCurrentBoardIdSet(currentBoardId);
        }

        lastBoardId.current = currentBoardId;
    }, [currentBoardId, stateCurrentBoardId, isCurrentBoardError]);

    // This effect updates the URL to match the current board, by adding a slug and permission ID if necessary
    useEffect(() => {
        // Don't update the URL when in preview mode
        if (!currentBoard || isPreviewRoute || isPublishPreviewRoute) return;

        // We're in an intermediate state, we need to wait for the current board to be updated to match the URL
        if (getElementId(currentBoard) !== currentBoardId) return;

        const title = getTitle(currentBoard);
        const slug = title && slugify(title);

        // If the current URL matches
        // eslint-disable-next-line eqeqeq
        if (currentUrlPermissionId == bestUrlPermissionId && routeSlug === slug) return;

        dispatchReplaceBoardUrl({
            boardId: currentBoardId,
            slug,
            permissionId: bestUrlPermissionId,
        });
    }, [currentBoard, bestUrlPermissionId]);

    return null;
};

CurrentBoardIdSynchroniser.propTypes = {
    currentBoard: PropTypes.object,
    stateCurrentBoardId: PropTypes.string,
    isPreviewRoute: PropTypes.bool,
    isPublishPreviewRoute: PropTypes.bool,
    dispatchCurrentBoardIdSet: PropTypes.func,
    dispatchReplaceBoardUrl: PropTypes.func,
    params: PropTypes.object.isRequired,
    routeParams: PropTypes.object.isRequired,
    isCurrentBoardError: PropTypes.bool,
    currentUrlPermissionId: PropTypes.string,
    bestUrlPermissionId: PropTypes.string,
};

export default connect(mapStateToProps, mapDispatchToProps)(CurrentBoardIdSynchroniser);
